import { EMPTY_FIELD } from 'constants/emptyField';
import { Resources } from 'constants/resources';

import { MerchantNotificationsEventNamesType } from 'config/requests/merchants';
import { usePermissions } from 'hooks/usePermissions';
import { useSendTestNotification } from 'pages/Merchants/MerchantsShow/MerchantsShowNotifications/hooks/useSendTestNotification';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { RecordContextProvider } from 'react-admin';
import { Button, DeleteButton } from 'shared/mui/Buttons';
import { CardActionsButtons } from 'shared/mui/Card';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { CardHeader } from 'shared/mui/Card/CardHeader/CardHeader';
import { CardTable } from 'shared/mui/Card/CardTableNew/CardTable';
import { Dialog } from 'shared/mui/Dialogs';
import { CloseIconButton } from 'shared/mui/IconButtons';
import { UserActionsIconButton } from 'shared/mui/IconButtons/UserActionsIconButton/UserActionsIconButton';
import { Link } from 'shared/mui/Link/Link';
import { TableRowWrapper } from 'shared/mui/Table/TableRowWrapperNew/TableRowWrapper';
import { BooleanField, DateTimeField, TextField } from 'shared/react-admin/Fields';
import { YesIcon } from 'shared/react-admin/Fields/BooleanField/BooleanField';
import { ReferenceField } from 'shared/react-admin/ReferenceFields';
import { MerchantNotification } from 'types/embedded/merchantNotification';
import { Permission } from 'types/embedded/permission';

type Props = {
  record: MerchantNotification;
  open: boolean;
  onEdit: (record: MerchantNotification) => void;
  onDelete: (record: MerchantNotification) => void;
  onClose: VoidFunction;
  onSendNotification: VoidFunction;
  eventNames?: MerchantNotificationsEventNamesType[];
};

export const MerchantsShowNotificationsShowDialog: React.FC<Props> = ({
  onClose,
  onDelete,
  onEdit,
  open,
  record,
  onSendNotification,
  eventNames,
}) => {
  const translate = useTypedTranslate();

  const { hasResourceAccess, hasResourcePermissions } = usePermissions();
  const { sendTestNotification } = useSendTestNotification(record.id, () => {
    onSendNotification();
    onClose();
  });

  return (
    <Dialog fullWidth maxWidth="sm" onClose={onClose} open={open}>
      <RecordContextProvider value={record}>
        <CardHeader
          appendActions={[
            hasResourceAccess(Resources.UserActions) && (
              <UserActionsIconButton
                entityId={record?.id}
                entityName="merchant_notification_settings"
                key="user-action-link"
              />
            ),
            <CloseIconButton key="close-button" onClose={onClose} />,
          ]}
          title={translate('app.merchantsShowNotifications.notificationSettingsView')}
          titleSize="smallBold"
        />
        <CardContent>
          <CardTable tableBodyProps={{ sx: { '& td': { width: '254px' } } }}>
            <TableRowWrapper label={translate('app.gatewayRules.active')}>
              <BooleanField source="active" />
            </TableRowWrapper>
            <TableRowWrapper label={translate('app.merchantsShowNotifications.channelId')}>
              <TextField source="credentials.chatId" />
            </TableRowWrapper>
            <TableRowWrapper label={translate('app.table.provider')}>
              <ReferenceField
                empty={EMPTY_FIELD}
                label={translate('app.table.provider')}
                reference="providers"
                sortable={false}
                source="provider.@id"
              >
                <TextField source="name" sx={{ textDecoration: 'underline' }} />
              </ReferenceField>
            </TableRowWrapper>
            <TableRowWrapper label={translate('app.table.createdAt')}>
              <DateTimeField inline source="createdAt" />
            </TableRowWrapper>
            <TableRowWrapper label={translate('app.table.updatedAt')}>
              <DateTimeField inline source="updatedAt" />
            </TableRowWrapper>
            {record?.events.map(({ name: { value } }) => (
              <TableRowWrapper
                key={value}
                label={eventNames?.find((eventName) => value === eventName.value)?.description}
              >
                <YesIcon />
              </TableRowWrapper>
            ))}
            <TableRowWrapper label={translate('app.table.comment')}>
              <TextField emptyText={EMPTY_FIELD} source="comment" sx={{ wordBreak: 'normal' }} />
            </TableRowWrapper>
            <TableRowWrapper label={translate('app.merchantsShowNotifications.testMessage')}>
              <Link onClick={sendTestNotification} sx={{ cursor: 'pointer' }}>
                {translate('app.buttons.continue')}
              </Link>
            </TableRowWrapper>
          </CardTable>
        </CardContent>
        <CardActionsButtons
          rightActionsSlot={[
            hasResourcePermissions(Resources.MerchantNotificationSettings, [
              Permission.Full,
              Permission.Delete,
            ]) && (
              <DeleteButton
                onClick={() => {
                  onClose();
                  onDelete(record);
                }}
                variant="outlined"
              />
            ),
            hasResourcePermissions(Resources.MerchantNotificationSettings, [
              Permission.Full,
              Permission.Update,
            ]) && (
              <Button
                label={translate('app.buttons.edit')}
                onClick={() => {
                  onClose();
                  onEdit(record);
                }}
                variant="contained"
              />
            ),
          ]}
        />
      </RecordContextProvider>
    </Dialog>
  );
};
